<script setup lang="ts">
import SingleOperationOfficeAutoComplete from '@/components/AutoCompletes/SingleOperationOfficeAutoComplete.vue';
import getUnexpectedEventAndAlertStatuses from "@/types/requests/getUnexpectedEventAndAlertStatuses.vue";
import ManagementOfficeAutoComplete from '@/components/AutoCompletes/ManagementOfficeAutoComplete.vue';
import DepartmentAutoComplete from '@/components/AutoCompletes/DepartmentAutoComplete.vue';
import { SELECTABLE_STATUSES } from '@/services/consts/unecpected_event_and_alert_status';
import VehicleAutoComplete from '@/components/AutoCompletes/VehicleAutoComplete.vue';
import DriverAutoComplete from '@/components/AutoCompletes/DriverAutoComplete.vue';
import GroupAutoComplete from '@/components/AutoCompletes/GroupAutoComplete.vue';
import ValidationErrors from '@/components/Common/ValidationErrors.vue';
import RangeDatePicker from '@/components/Common/RangeDatePicker.vue';
import SelectBox from "@/components/Common/selectBox.vue";
import { computed } from 'vue';
import i18n from '@/lang/i18n';

const emits = defineEmits(['update:modelValue'])
const props = defineProps<{
  modelValue: getUnexpectedEventAndAlertStatuses
  maxFilterDays: number
  errors: any
  operationOfficeClearable: boolean
  isManagementOffice: boolean
  isDepartment: boolean
}>()

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emits('update:modelValue', value);
  },
})

</script>
<template>
  <ManagementOfficeAutoComplete
    v-model="modelValue.managementOffice"
    selectFirstIfOnlyOne
    :clearable="props.isManagementOffice"
  />
  <SingleOperationOfficeAutoComplete
    v-model="modelValue.operationOffice"
    :managementOffice="modelValue.managementOffice"
    :error-messages="errors.operationOffice"
    selectFirstIfOnlyOne
    :clearable="props.operationOfficeClearable"
  />
  <GroupAutoComplete
    v-model="modelValue.group"
    :managementOffice="modelValue.managementOffice"
    :operationOffices="[modelValue.operationOffice]"
  />
  <DepartmentAutoComplete
    v-model="modelValue.department"
    :operationOffices="[modelValue.operationOffice]"
    :selectFirstIfOnlyOne="isDepartment"
  />
  <VehicleAutoComplete
    v-model="modelValue.vehicle"
    :selectedDepartment="modelValue.department"
    :managementOffice="modelValue.managementOffice"
    :operationOffices="[modelValue.operationOffice]"
    :requestParams="{ type: 'filter' }"
  />
  <DriverAutoComplete
    v-model="modelValue.driver"
    :selectedDepartment="modelValue.department"
    :operationOffices="modelValue.operationOffice ? [modelValue.operationOffice] : []"
  />
  <SelectBox
    class="pt-2"
    v-model="modelValue.division"
    :label="i18n.global.t('pages.unexpectedEventAndAlertStatuses.filter.selectDivision.title')"
    :items="SELECTABLE_STATUSES"
    :clearable="false"
  />
  <div class="p-1">
    <v-radio-group
      v-model="modelValue.includes_start_or_end_time"
      class="radio-group-row"
    >
      <v-radio
        value="start_time"
        :label="i18n.global.t('pages.common.filter.startTime')"
        color="blue"
      ></v-radio>
      <v-radio
        value="end_time"
        :label="i18n.global.t('pages.common.filter.endTime')"
        color="blue"
      />
    </v-radio-group>
  </div>
  <validation-errors :messages="errors.start_time">
    <div style="font-size: 12px" class="d-flex justify-space-between m-1">
      <RangeDatePicker
        v-model="modelValue.dateRange"
        :maxRange="props.maxFilterDays"
        :format="'yyyy/MM/dd'"
        :isError="errors.start_time?.length > 0"
      />
    </div>
  </validation-errors>
</template>
